import { useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import FaqAccordion from "@/components/feature/FaqAccordion";
import LeadForm from "@/components/feature/LeadForm";
import StructuredData from "@/components/feature/StructuredData";
import PageMeta from "@/components/feature/PageMeta";
import type { ServiceDetail } from "@/mocks/services";
import { contactInfo } from "@/mocks/site";
import { areaAnchorId, areaPageSlug } from "@/utils/areaSlug";
import { resolveSeo } from "@/utils/seo";
import { buildBreadcrumbSchema } from "@/utils/schemaBuilders";
import { HERO_IMAGES, SERVICE_HERO_IMAGES } from "@/config/heroImages";

const QUOTE_FORM_ID = "quote-form";

export default function ServicePageTemplate({ detail, areaMap }: { detail: ServiceDetail; areaMap?: ReactNode }) {
  const location = useLocation();

  const crumbs = useMemo(
    () => [
      { label: "Home", href: "/" },
      { label: "Services", href: "/services" },
      { label: detail.crumb },
    ],
    [detail.crumb],
  );

  const breadcrumbSchema = useMemo(() => buildBreadcrumbSchema(crumbs), [crumbs]);

  // Reuse the central SEO config so service titles/descriptions stay concise
  // (the raw intro paragraph is too long for a meta description).
  const seo = useMemo(() => resolveSeo(`/services/${detail.slug}`), [detail.slug]);

  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: detail.faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    }),
    [detail.faqs],
  );

  useEffect(() => {
    if (!location.hash) return undefined;
    const target = document.getElementById(location.hash.slice(1));
    if (!target) return undefined;
    const timer = window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <>
      <PageMeta
        title={seo.title}
        description={seo.description}
        path={`/services/${detail.slug}`}
        image={detail.image}
      />
      <StructuredData id="ld-service-breadcrumb" data={breadcrumbSchema} />
      <StructuredData id="ld-service-faq" data={faqSchema} />
      <PageHero
        eyebrow={detail.eyebrow}
        title={detail.title}
        subtitle={detail.tagline}
        crumbs={crumbs}
        image={SERVICE_HERO_IMAGES[detail.slug] ?? HERO_IMAGES.services}
      />

      {/* Intro + image */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
            <div className="flex flex-col">
              <SectionHeading
                align="left"
                eyebrow="Service Overview"
                title={`Why families & businesses choose our ${detail.crumb.toLowerCase()}`}
              />
              <div className="mt-5 space-y-4">
                {detail.intro.map((paragraph) => (
                  <p key={paragraph} className="text-sm leading-relaxed text-foreground-600 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={contactInfo.phoneHref}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-phone-fill text-lg" aria-hidden="true" /> Call {contactInfo.phone}
                </a>
                <Link
                  to="/request-a-quote"
                  data-cta="Get a Free Quote"
                  data-cta-location="service-hero"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-file-text-line text-lg" aria-hidden="true" /> Get a Free Quote
                </Link>
              </div>
            </div>

            <div>
              <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200">
                <img
                  src={detail.image}
                  alt={detail.title}
                  title={detail.title}
                  className="h-full w-full object-cover object-center"
                />
              </div>
              <div className="mt-3 flex items-center justify-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs font-medium text-foreground-600">
                <i className="ri-image-line text-sm text-primary-500" aria-hidden="true" />
                {detail.title}
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {detail.highlights.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-accent-200 bg-accent-50 p-4"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>

          {detail.gallery && detail.gallery.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                A closer look at this service
              </h3>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {detail.gallery.map((src, index) => (
                  <div
                    key={src}
                    className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200"
                  >
                    <img
                      src={src}
                      alt={`${detail.title} in action ${index + 1}`}
                      title={detail.title}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What's included */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-14">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                Everything included in this service
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                One team handles the entire job end to end, so you are not coordinating between multiple vendors on
                your moving day.
              </p>
              <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {detail.includes.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 rounded-md border border-background-200 bg-background-50 px-3.5 py-3 text-sm text-foreground-700"
                  >
                    <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
              <h3 className="font-heading text-lg font-bold text-foreground-950">What decides your cost</h3>
              <p className="mt-2 text-sm text-foreground-600">
                We quote after understanding your exact requirement, so your estimate stays honest.
              </p>
              <ul className="mt-5 space-y-3">
                {detail.costFactors.map((factor) => (
                  <li key={factor} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-arrow-right-circle-line mt-0.5 text-accent-500" aria-hidden="true" />
                    {factor}
                  </li>
                ))}
              </ul>
              <Link
                to="/request-a-quote"
                data-cta="Get My Estimate"
                data-cta-location="service-sidebar"
                className="mt-6 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-calculator-line text-lg" aria-hidden="true" /> Get My Estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading eyebrow="How It Works" title="Our 4 step moving process" />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {detail.process.map((step, index) => (
              <div key={step.title} className="relative rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="font-heading text-3xl font-extrabold text-primary-200">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ + form */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              Frequently asked questions
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              The questions our customers ask most about this service.
            </p>
            <div className="mt-6">
              <FaqAccordion items={detail.faqs} />
            </div>
          </div>
          <LeadForm
            formId={QUOTE_FORM_ID}
            title="Request a free quote"
            subtitle={`Tell us about your ${detail.crumb.toLowerCase()} requirement and we will get back with a clear estimate.`}
            submitLabel="REQUEST A FREE QUOTE"
            serviceOptions={["House Shifting", "Office Shifting", "Storage Service", "Warehouse Facility", "Other"]}
          />
        </div>
      </section>

      {/* Areas we serve */}
      {detail.areas && detail.areas.length > 0 && (
        <section className="bg-background-50 py-14 md:py-16">
          <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
            <SectionHeading
              eyebrow={detail.areasEyebrow ?? "Areas We Serve"}
              title={detail.areasHeading ?? "Localities we cover"}
              subtitle={detail.areasIntro}
            />
            {areaMap}
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {detail.areas.map((area) => (
                <Link
                  key={area.name}
                  to={`/areas-we-serve/${areaPageSlug(area.name)}`}
                  id={areaAnchorId(area.name)}
                  className="group scroll-mt-28 rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-accent-300"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-map-pin-2-line text-lg" aria-hidden="true" />
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      Local Shifting in {area.name}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{area.text}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
                    View {area.name} page
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
            <Link
              to="/areas-we-serve"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-5 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
            >
              <i className="ri-map-2-line text-lg" aria-hidden="true" /> See all areas we serve
            </Link>
          </div>
        </section>
      )}

      <CtaBanner />
    </>
  );
}